import { useEffect, useLayoutEffect, useRef, useState } from 'react';
import { buildPopup, placePopup, type Placement } from '../../lib/popup';
import { useMapStore } from '../../state/mapStore';

export function IdentifyPopup() {
  const popup = useMapStore((s) => s.popup);
  const layer = useMapStore((s) => (s.popup ? s.layers.get(s.popup.layerId) : undefined));
  const close = useMapStore((s) => s.setPopup);
  const ref = useRef<HTMLDivElement>(null);
  const [placement, setPlacement] = useState<Placement | null>(null);

  // Measure after render, then place: above the point, or below it when the top of the map would clip it.
  useLayoutEffect(() => {
    const el = ref.current;
    const parent = el?.offsetParent;
    if (!popup || !el || !(parent instanceof HTMLElement)) return;
    setPlacement(
      placePopup(
        popup.point,
        { width: el.offsetWidth, height: el.offsetHeight },
        { width: parent.clientWidth, height: parent.clientHeight },
      ),
    );
  }, [popup]);

  useEffect(() => {
    if (!popup) return;
    const onKey = (e: KeyboardEvent): void => {
      if (e.key === 'Escape') close(null);
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [popup, close]);

  if (!popup || !layer) return null;
  const content = buildPopup(layer, popup.properties);
  return (
    <div
      ref={ref}
      className="popup"
      style={
        placement
          ? { left: placement.left, top: placement.top }
          : { left: popup.point.x, top: popup.point.y, visibility: 'hidden' }
      }
      role="dialog"
      aria-label={content.heading}
    >
      <div className="popup-head">
        <h2>{content.heading}</h2>
        <button type="button" className="icon-btn" aria-label="Close" onClick={() => close(null)}>
          ×
        </button>
      </div>
      {content.rows.length > 0 && (
        <dl>
          {content.rows.map((r) => (
            <div key={r.field} className="popup-row">
              <dt>{r.field}</dt>
              <dd>{r.value}</dd>
            </div>
          ))}
        </dl>
      )}
    </div>
  );
}
